import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { doc, getDoc, collection, getDocs } from 'firebase/firestore';
import { useAuth } from '../contexts/authContext';
import { ArrowLeftIcon, CheckCircleIcon, PlayCircleIcon } from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleSolid } from '@heroicons/react/24/solid';

const SuggestedContent = () => {
  const { discipleId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  
  const [disciple, setDisciple] = useState(null);
  const [contentTrack, setContentTrack] = useState(null);
  const [contentResources, setContentResources] = useState([]);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedResource, setSelectedResource] = useState(null);

  useEffect(() => {
    loadData();
  }, [discipleId, currentUser]);

  const loadData = async () => {
    if (!currentUser || !discipleId) return;

    try {
      setLoading(true);

      // Load disciple
      const discipleDoc = await getDoc(doc(db, 'disciples', discipleId));
      if (discipleDoc.exists()) {
        setDisciple({ id: discipleDoc.id, ...discipleDoc.data() });
      }

      // Load mentor details to get subscribed track
      const userAppID = currentUser.appID || currentUser.uid;
      const mentorDetailsDoc = await getDoc(
        doc(db, 'disciples', discipleId, 'mentorDetails', userAppID)
      );

      if (!mentorDetailsDoc.exists()) {
        alert('No Track Subscription', 'This disciple is not subscribed to any content track.');
        navigate(-1);
        return;
      }

      const mentorData = mentorDetailsDoc.data();
      // First check subscribedContentTrackId field (primary method)
      let trackId = mentorData?.subscribedContentTrackId;
      
      // If not found, check subscribedTracks subcollection as fallback
      if (!trackId) {
        try {
          const subscribedTracksSnapshot = await getDocs(
            collection(db, 'disciples', discipleId, 'mentorDetails', userAppID, 'subscribedTracks')
          );
          
          if (!subscribedTracksSnapshot.empty) {
            // Use the first subscribed track (most recent)
            trackId = subscribedTracksSnapshot.docs[0].id;
          }
        } catch (error) {
          console.error('Error checking subscribedTracks subcollection:', error);
        }
      }

      if (!trackId) {
        alert('No Track Subscription', 'This disciple is not subscribed to any content track.');
        navigate(-1);
        return;
      }

      // Load track details
      const trackDoc = await getDoc(doc(db, 'contentTracks', trackId));

      if (trackDoc.exists()) {
        setContentTrack({ id: trackDoc.id, ...trackDoc.data() });
      }

      // Load progress
      const progressData = mentorData?.contentTrackProgress;
      if (progressData) {
        setProgress(progressData);
      }

      // Load resources from track
      const resourcesSnapshot = await getDocs(
        collection(db, 'contentTracks', trackId, 'resources')
      );

      // Filter for published resources and sort by displayOrder
      const resources = resourcesSnapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(resource => resource.status === 'published' || !resource.status)
        .sort((a, b) => {
          const orderA = a.displayOrder ?? 999;
          const orderB = b.displayOrder ?? 999;
          return orderA - orderB;
        });

      setContentResources(resources);

      // Auto-select the next unsent resource if available
      if (progressData && resources.length > 0) {
        const sentIds = progressData.sentResourceIds || [];
        const nextUnsent = resources.find(r => !sentIds.includes(r.id));
        if (nextUnsent) {
          setSelectedResource(nextUnsent);
        } else if (resources.length > 0) {
          setSelectedResource(resources[0]);
        }
      } else if (resources.length > 0) {
        setSelectedResource(resources[0]);
      }

    } catch (error) {
      console.error('Error loading content:', error);
      alert('Error', 'Failed to load content track information');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateMessage = () => {
    if (!selectedResource) {
      alert('No Content Selected', 'Please select a content piece to send.');
      return;
    }

    navigate(`/send-content-message/${discipleId}/${selectedResource.id}`);
  };

  const isResourceSent = (resourceId) => {
    return progress?.sentResourceIds?.includes(resourceId) || false;
  };

  const isNextUnsent = (resourceId) => {
    if (!progress) return false;
    const sentIds = progress.sentResourceIds || [];
    const unsentResources = contentResources.filter(r => !sentIds.includes(r.id));
    return unsentResources.length > 0 && unsentResources[0].id === resourceId;
  };

  const getVideoThumbnail = (url) => {
    if (!url) return null;
    const urlLower = url.toLowerCase();
    
    // Handle YouTube URLs (including Shorts)
    if (urlLower.includes('youtube.com') || urlLower.includes('youtu.be')) {
      // Try YouTube Shorts format first: youtube.com/shorts/VIDEO_ID
      let videoId = url.match(/(?:youtube\.com\/shorts\/)([a-zA-Z0-9_-]{11})/i)?.[1];
      
      // If not Shorts, try standard formats
      if (!videoId) {
        videoId = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/)?.[1];
      }
      
      // Fallback: try to extract from v= parameter
      if (!videoId) {
        videoId = url.match(/[?&]v=([a-zA-Z0-9_-]{11})/)?.[1];
      }
      
      if (videoId) {
        // Try maxresdefault first, but it might not be available for all videos
        return `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
      }
    }
    
    // Handle Vimeo URLs
    if (urlLower.includes('vimeo.com/')) {
      const videoId = url.match(/vimeo\.com\/(\d+)/)?.[1];
      return videoId ? `https://vumbnail.com/${videoId}.jpg` : null;
    }
    
    return null;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  if (!contentTrack || contentResources.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="flex items-center p-4 border-b bg-white">
          <button onClick={() => navigate(-1)} className="mr-4">
            <ArrowLeftIcon className="h-6 w-6 text-gray-700" />
          </button>
          <h1 className="text-xl font-bold text-gray-900">Select Content</h1>
        </div>
        <div className="flex flex-col items-center justify-center p-12">
          <div className="text-gray-400 mb-4">📚</div>
          <div className="text-lg font-semibold text-gray-700 mb-2">No content available</div>
          <div className="text-sm text-gray-500 text-center">
            This track doesn't have any published content yet.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="flex items-center p-4 border-b bg-white">
        <button onClick={() => navigate(-1)} className="mr-4">
          <ArrowLeftIcon className="h-6 w-6 text-gray-700" />
        </button>
        <h1 className="text-xl font-bold text-gray-900">Select Content</h1>
      </div>

      <div className="p-4 pb-24">
        {/* Track Info */}
        <div className="bg-white rounded-xl p-4 mb-4 shadow-sm">
          <h2 className="text-lg font-bold text-gray-900 mb-2">{contentTrack.name}</h2>
          {contentTrack.description && (
            <p className="text-sm text-gray-600 mb-2">{contentTrack.description}</p>
          )}
          {progress && (
            <p className="text-sm font-semibold" style={{ color: '#0076BE' }}>
              Progress: {progress.sentCount}/{progress.totalPublishedResources} sent
            </p>
          )}
        </div>

        {/* Resources List */}
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Available Content</h3>
          {contentResources.map((resource, index) => {
            const isSent = isResourceSent(resource.id);
            const isNext = isNextUnsent(resource.id);
            const isSelected = selectedResource?.id === resource.id;

            // Check multiple possible URL field names
            const videoUrl = resource.url || 
                           resource.videoUrl || 
                           resource.link || 
                           resource.video ||
                           resource.videoLink ||
                           resource.contentUrl ||
                           resource.mediaUrl ||
                           '';
            
            const videoUrlLower = videoUrl ? String(videoUrl).toLowerCase() : '';
            const resourceTypeLower = resource.type ? String(resource.type).toLowerCase() : '';
            
            const isVideo = resourceTypeLower === 'video' || 
                          (videoUrl && (
                            videoUrlLower.includes('youtube') || 
                            videoUrlLower.includes('youtu.be') || 
                            videoUrlLower.includes('vimeo') ||
                            videoUrlLower.includes('youtube.com') ||
                            videoUrlLower.includes('youtu.be/') ||
                            videoUrlLower.includes('vimeo.com')
                          ));
            
            // Always try to get thumbnail if we have a URL - let getVideoThumbnail handle detection
            // This ensures we catch videos even if type field is missing or URL format is non-standard
            const thumbnailUrl = videoUrl && videoUrl.trim() ? getVideoThumbnail(String(videoUrl)) : null;

            return (
              <div
                key={resource.id}
                onClick={() => setSelectedResource(resource)}
                className={`bg-white rounded-xl p-4 mb-3 border-2 cursor-pointer transition ${
                  isSelected ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
                } ${isNext ? 'border-blue-500' : ''}`}
              >
                <div className="flex items-start">
                  <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center mr-3 flex-shrink-0">
                    <span className="text-sm font-bold" style={{ color: '#0076BE' }}>
                      {index + 1}
                    </span>
                  </div>
                  
                  {thumbnailUrl && (
                    <div className="w-32 h-20 rounded-lg overflow-hidden mr-3 flex-shrink-0 relative">
                      <img src={thumbnailUrl} alt={resource.title} className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center">
                        <PlayCircleIcon className="h-8 w-8 text-white" />
                      </div>
                    </div>
                  )}
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between mb-2">
                      <h4 className="text-base font-semibold text-gray-900 flex-1">
                        {resource.title || `Content ${index + 1}`}
                      </h4>
                      {isSelected && (
                        <CheckCircleSolid className="h-6 w-6 flex-shrink-0" style={{ color: '#0076BE' }} />
                      )}
                    </div>
                    {resource.type && !thumbnailUrl && (
                      <div className="inline-flex items-center px-2 py-1 rounded-md bg-blue-100 mb-2">
                        <span className="text-xs font-semibold capitalize" style={{ color: '#0076BE' }}>
                          {resource.type}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Status Indicators */}
                <div className="flex items-center gap-2 mt-3">
                  {isNext && (
                    <div className="inline-flex items-center px-2 py-1 rounded-full bg-blue-500 text-white text-xs font-semibold">
                      <span>→</span>
                      <span className="ml-1">Next to Send</span>
                    </div>
                  )}
                  {isSent && (
                    <div className="inline-flex items-center px-2 py-1 rounded-full bg-blue-100 text-xs font-semibold" style={{ color: '#0076BE' }}>
                      <CheckCircleIcon className="h-3 w-3 mr-1" />
                      <span>Sent</span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Create Message Button */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t p-4">
        <button
          onClick={handleCreateMessage}
          disabled={!selectedResource}
          className={`w-full py-4 px-6 rounded-xl font-semibold text-white transition ${
            selectedResource 
              ? 'bg-green-500 hover:bg-green-600' 
              : 'bg-gray-300 cursor-not-allowed'
          }`}
          style={{ backgroundColor: selectedResource ? '#5ABA47' : undefined }}
        >
          {selectedResource 
            ? `Create Message with "${selectedResource.title || 'Content'}"` 
            : 'Select Content'}
        </button>
      </div>
    </div>
  );
};

export default SuggestedContent;




